import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCloudComputerCreateOptions,
  useCreateCloudComputer,
  useDeleteCloudComputer,
  useResizeCloudComputer,
  type CloudComputer,
  type CloudComputerOption,
  type CloudComputerProvider,
} from "@/hooks/useCloudComputers";

const NO_KEY_PAIR = "__none";
const DEFAULT_VOLUME_GB = 20;

export function CreateCloudComputerDialog({
  provider,
  onOpenChange,
}: {
  provider: CloudComputerProvider | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, error } = useCloudComputerCreateOptions(
    provider?.id ?? null,
  );
  const createComputer = useCreateCloudComputer();
  const options = data?.options;

  const [name, setName] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [imageId, setImageId] = useState("");
  const [regionId, setRegionId] = useState("");
  const [networkId, setNetworkId] = useState("");
  const [keyPair, setKeyPair] = useState(NO_KEY_PAIR);
  const [securityGroups, setSecurityGroups] = useState<string[]>([]);
  const [diskSizeGb, setDiskSizeGb] = useState("");
  const [publicIp, setPublicIp] = useState(true);

  // Reset the form whenever it opens for a provider.
  useEffect(() => {
    if (!provider) return;
    setName("");
    setSizeId("");
    setImageId("");
    setKeyPair(NO_KEY_PAIR);
    setDiskSizeGb("");
    setPublicIp(true);
  }, [provider]);

  useEffect(() => {
    if (!options) return;
    setRegionId(options.regions[0]?.id ?? "");
    setNetworkId(options.networks[0]?.id ?? "");
    setSecurityGroups(options.defaultSecurityGroups);
  }, [options]);

  const size = options?.sizes.find((option) => option.id === sizeId);
  const image = options?.images.find((option) => option.id === imageId);
  // Fixed disk sizes (Paperspace) or a boot volume for sizes without local disk (Infomaniak).
  const needsVolumeSize = !options?.diskSizesGb && size?.diskGb === 0;
  const minVolumeGb = Math.max(1, image?.minDiskGb ?? 1);
  const volumeGb = Number(
    diskSizeGb || Math.max(DEFAULT_VOLUME_GB, minVolumeGb),
  );

  const canSubmit =
    Boolean(name.trim() && sizeId && imageId) &&
    (options?.regions.length === 0 || Boolean(regionId)) &&
    (options?.networks.length === 0 || Boolean(networkId)) &&
    (!options?.diskSizesGb || Boolean(diskSizeGb)) &&
    (!needsVolumeSize || volumeGb >= minVolumeGb);

  const submit = () => {
    if (!provider || !options) return;
    createComputer.mutate(
      {
        providerId: provider.id,
        input: {
          name: name.trim(),
          sizeId,
          imageId,
          regionId: regionId || undefined,
          networkId: networkId || undefined,
          keyPair: keyPair === NO_KEY_PAIR ? undefined : keyPair,
          securityGroups: options.securityGroups.length
            ? securityGroups
            : undefined,
          diskSizeGb: options.diskSizesGb
            ? Number(diskSizeGb)
            : needsVolumeSize
              ? volumeGb
              : undefined,
          publicIp,
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={provider !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New {provider?.label} computer</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">
            Loading options...
          </div>
        ) : isError ? (
          <div className="text-sm text-destructive">
            {error instanceof Error ? error.message : "Failed to load options"}
          </div>
        ) : options ? (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) submit();
            }}
          >
            <Field label="Name">
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="lm-cli1-example"
                autoFocus
              />
            </Field>
            <OptionSelect
              label="Size"
              value={sizeId}
              onChange={setSizeId}
              options={options.sizes}
            />
            <OptionSelect
              label="Image"
              value={imageId}
              onChange={setImageId}
              options={options.images}
            />
            {options.regions.length > 0 && (
              <OptionSelect
                label="Region"
                value={regionId}
                onChange={setRegionId}
                options={options.regions}
              />
            )}
            {options.diskSizesGb && (
              <OptionSelect
                label="Disk"
                value={diskSizeGb}
                onChange={setDiskSizeGb}
                options={options.diskSizesGb.map((gb) => ({
                  id: String(gb),
                  label: `${gb} GB`,
                }))}
              />
            )}
            {needsVolumeSize && (
              <Field label="Boot volume (GB)">
                <Input
                  type="number"
                  min={minVolumeGb}
                  value={diskSizeGb || String(volumeGb)}
                  onChange={(event) => setDiskSizeGb(event.target.value)}
                />
              </Field>
            )}
            {options.networks.length > 1 && (
              <OptionSelect
                label="Network"
                value={networkId}
                onChange={setNetworkId}
                options={options.networks}
              />
            )}
            {options.keyPairs.length > 0 && (
              <OptionSelect
                label="SSH key"
                value={keyPair}
                onChange={setKeyPair}
                options={[
                  { id: NO_KEY_PAIR, label: "None" },
                  ...options.keyPairs,
                ]}
              />
            )}
            {options.securityGroups.length > 0 && (
              <Field label="Security groups">
                <div className="grid grid-cols-2 gap-2">
                  {options.securityGroups.map((group) => (
                    <label
                      key={group.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={securityGroups.includes(group.id)}
                        onCheckedChange={(checked) =>
                          setSecurityGroups((current) =>
                            checked
                              ? [...current, group.id]
                              : current.filter((id) => id !== group.id),
                          )
                        }
                      />
                      {group.label}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={publicIp}
                onCheckedChange={(checked) => setPublicIp(checked === true)}
              />
              Assign a public IP
            </label>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || createComputer.isPending}
              >
                {createComputer.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function DeleteCloudComputerDialog({
  computer,
  onOpenChange,
}: {
  computer: CloudComputer | null;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteComputer = useDeleteCloudComputer();
  const [confirmName, setConfirmName] = useState("");
  const [releasePublicIp, setReleasePublicIp] = useState(true);

  useEffect(() => {
    setConfirmName("");
    setReleasePublicIp(true);
  }, [computer]);

  const nameMatches = computer !== null && confirmName === computer.name;
  const hasPublicIp = (computer?.publicIps.length ?? 0) > 0;

  const submit = () => {
    if (!computer || !nameMatches) return;
    deleteComputer.mutate(
      { computer, confirmName, releasePublicIp },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={computer !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {computer?.name}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the computer. It cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field
            label={
              <>
                Type <span className="font-mono">{computer?.name}</span> to
                confirm
              </>
            }
          >
            <Input
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              autoComplete="off"
              autoFocus
              data-testid="delete-confirm-name"
            />
          </Field>
          {hasPublicIp && computer?.provider === "infomaniak" && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={releasePublicIp}
                onCheckedChange={(checked) =>
                  setReleasePublicIp(checked === true)
                }
              />
              Also release public IP {computer.publicIps.join(", ")}
            </label>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!nameMatches || deleteComputer.isPending}
            >
              {deleteComputer.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Size options are labelled "<size name> · …"; the computer only knows the size name. */
function sizeName(option: CloudComputerOption) {
  return option.label.split(" · ")[0];
}

export function ResizeCloudComputerDialog({
  computer,
  onOpenChange,
}: {
  computer: CloudComputer | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, error } = useCloudComputerCreateOptions(
    computer?.provider ?? null,
  );
  const resizeComputer = useResizeCloudComputer();
  const [sizeId, setSizeId] = useState("");
  const options = data?.options;
  const current = options?.sizes.find(
    (option) => sizeName(option) === computer?.size,
  );

  useEffect(() => {
    setSizeId(current?.id ?? "");
  }, [computer, current?.id]);

  const selected = options?.sizes.find((option) => option.id === sizeId);
  const mustStop =
    options?.resizeRequiresStop === true && computer?.state !== "stopped";
  const isTransitioning =
    computer !== null &&
    computer.state !== "running" &&
    computer.state !== "stopped";
  const canSubmit =
    Boolean(selected) &&
    selected?.id !== current?.id &&
    !mustStop &&
    !isTransitioning;

  const submit = () => {
    if (!computer || !selected || !canSubmit) return;
    resizeComputer.mutate(
      { computer, sizeId: selected.id, sizeLabel: sizeName(selected) },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={computer !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resize {computer?.name}</DialogTitle>
          <DialogDescription>
            Current size: {computer?.size ?? "unknown"}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading sizes...</div>
        ) : isError ? (
          <div className="text-sm text-destructive">
            {error instanceof Error ? error.message : "Failed to load sizes"}
          </div>
        ) : options ? (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <OptionSelect
              label="New size"
              value={sizeId}
              onChange={setSizeId}
              options={options.sizes.map((option) =>
                option.id === current?.id
                  ? { ...option, label: `${option.label} (current)` }
                  : option,
              )}
            />
            {mustStop ? (
              <p className="text-sm text-muted-foreground">
                Stop the computer before resizing it.
              </p>
            ) : isTransitioning ? (
              <p className="text-sm text-muted-foreground">
                Wait until the computer is running or stopped.
              </p>
            ) : (
              computer?.state === "running" && (
                <p className="text-sm text-muted-foreground">
                  The computer restarts during the resize. Anything running on
                  it will be interrupted.
                </p>
              )
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || resizeComputer.isPending}
              >
                {resizeComputer.isPending ? "Resizing..." : "Resize"}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function OptionSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: CloudComputerOption[];
}) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
